// import Hecate from '@bybit/hecate';
import { normalizeLink } from './i18n';
import { isApp, isBrowser, isMobile } from './env';
import { useRouter } from 'next/router';
import { triggerOneLink, loadJsHecateScript } from './appflyers';
import { WalletInfoByApp, BridgeParams, BridgeRes } from '../types';

declare const window: any;

export const bybitHecate = (
  module: string,
  method: string,
  params: Record<string, any>
): Promise<BridgeRes<any>> =>
  // eslint-disable-next-line
  new Promise(async (resolve) => {
    if (!isApp()) {
      resolve({
        code: -1,
      });
      return;
    }

    const { default: Hecate } = await import('@bybit/hecate');

    Hecate.module(module)
      .method(method)
      .invoke(params)
      .then((res: any) => {
        console.log(`app invoke res: ${module}`, res);
        resolve(res);
      });
  });

export const openInsLogin = async () => {
  // bybitapp://open/login
  try {
    bybitHecate('Web3WalletBridge', 'openDeeplink', {
      app_name: 'Web3',
      deeplink: 'bybitapp://open/login', // 跳转到铭文搜索页面
    })
      .then((res: any) => {
        setTimeout(() => {
          window.parent.location.reload();
        }, 500);
      })
      .catch(() => {
        console.log('login:error');
      });
  } catch (error) {
    console.error(error);
  }
};

export const openAppLogin = async (onLoginSuccessCb?: (res: any) => void) => {
  try {
    bybitHecate('UserProfile', 'getUserProfile', { needLogin: true })
      .then((res: any) => {
        // 当前页面刷新
        console.log('getUserProfile', JSON.stringify(res));
        onLoginSuccessCb?.(res);
        if (res.params.uid) {
          setTimeout(() => {
            window.parent.location.reload();
          }, 500);
        }
      })
      .catch(() => {
        console.log('getUserProfile:error');
      });
  } catch (error) {
    console.error(error);
  }
};

export const BY_APP_PROTOCOL = 'bybitapp://';
export const BY_APP_INNER_PROTOCOL = ['by-mini://', 'by://'];
export const DownloadLink = `/download?utm_source=web3homepaged&utm_medium=picscann&utm_campaign=appdownload`;

export const openLink = async (path: string) => {
  const { setConfig, openApp } = await import('@sfe/bone-portal');
  setConfig({
    needView: true,
    downloadLink: DownloadLink,
  });

  const route = BY_APP_INNER_PROTOCOL.some((p) => path.startsWith(p))
    ? `${BY_APP_PROTOCOL}open/route?targetUrl=${encodeURIComponent(path)}`
    : path;
  if (/bybit_app/.test(navigator.userAgent.toLowerCase())) {
    bybitHecate('Router', 'push', { path: route });
    return;
  }
  return openApp(route);
};

export const downloadPath = '/download';

export const getWalletInfoByApp = (
  params: BridgeParams = {}
): Promise<BridgeRes<WalletInfoByApp>> => {
  const { bizType = 'REWARD' } = params;

  return bybitHecate('Web3WalletBridge', 'selectedwallet', {
    app_name: 'web3',
    bizType,
  });
};

export const getUserInfoByApp = (): Promise<any> => {
  return bybitHecate('UserProfile', 'getUserProfile', { needLogin: false });
};

/**
 * 在APP中，登陆、创建、切换钱包都是这一个api
 */
export const switchWalletByApp = (params: BridgeParams = {}) => {
  const {
    bizType = 'REWARD',
    walletTypeListSupport = ['CLOUD', 'MNEMONIC_PHRASE', 'MPC'],
  } = params;

  return bybitHecate('Web3WalletBridge', 'switchWallet', {
    app_name: 'web3',
    bizType,
    walletTypeListSupport,
  });
};

export const onWalletChangeByApp = async (
  onWalletChange?: (v: WalletInfoByApp) => void
) => {
  try {
    await bybitHecate('Web3WalletBridge', 'subSelectedWalletChanged', {
      app_name: 'web3',
    });
    if (onWalletChange) {
      window.onWalletChange = onWalletChange;
    }
  } catch (e) {
    // console.log(e)
  }
};

export const openOutsideBrowserByApp = (url: string) => {
  return bybitHecate('UrlLauncher', 'open', {
    url,
    externalApplication: true,
  });
};

export const initTwitterAuthByApp = (): Promise<
  BridgeRes<{}> & { uniqueId: string }
> => {
  return bybitHecate('Share', 'initTwitterAuth', {
    app_name: 'web3',
  }) as any;
};

export const versionIsSupportByApp = (version: string) => {
  return bybitHecate('SystemInfo', 'getAppConfig', {}).then((res: any) => {
    const { appVersion = '' } = res?.params || res || {};
    const appSupported = gte(appVersion, version);
    return appSupported;
  });
};

interface SignMessageParams {
  raw_data: string;
  sign_type: string;
  chain_id: number;
}

export const signMessageByApp = (
  params: SignMessageParams
): Promise<BridgeRes<string>> => {
  return bybitHecate('Web3WalletBridge', 'signMessage', {
    app_name: 'Web3',
    ...params,
  });
};

interface SignTransactionParams {
  data: string;
  chain_id: number;
  to: string;
  amount: string;
  gas_limit: string;
  gas_price: string;
}

export const signTransactionByApp = (params: SignTransactionParams) => {
  return bybitHecate('Web3WalletBridge', 'signEvmTransaction', {
    app_name: 'Web3',
    ...params,
  });
};

export const web3PurchaseWallet = async (
  chainCode = 'BTC',
  coinName = 'BTC',
  onSuccessCb?: () => void,
  onFailedCb?: () => void
) => {
  try {
    bybitHecate('Web3WalletBridge', 'web3Deposit', {
      app_name: 'web3',
      chainCode,
      coinName,
      contractAddress: '',
    })
      .then((res: any) => {
        console.log('web3Deposit', res);
        onSuccessCb?.();
      })
      .catch(() => {
        console.log('web3Deposit:error');
        onFailedCb?.();
      });
  } catch (error) {
    console.error(error);
  }
};

let token = '';
export const getAppUserToken = async () => {
  if (isApp()) {
    if (!token) {
      return new Promise((resolve) => {
        bybitHecate('UserProfile', 'getUserProfile', { needLogin: false })
          .then((res: any) => {
            console.log('jsBridge:getUserProfile', res);
            token = res.params ? res.params.userToken || '' : '';
            resolve(token);
          })
          .catch(({ message }: { message: string }) => {
            token = '';
            console.log('jsBridge:getUserProfile:error', message);
            resolve(token);
          });
      });
    } else {
      console.log('@@@@@@@');
    }
    return Promise.resolve(token);
  } else {
    return Promise.resolve('');
  }
};

export const jumpToSearchPage = (params: Record<string, string> = {}) => {
  const query = Object.keys(params)
    .map((key) => `${key}=${params[key]}`)
    .join('&');
  const router = useRouter();
  const path = 'by://web3/inscription/search';
  console.log('jumpToSearchPage params:', params, query);
  try {
    bybitHecate('Web3WalletBridge', 'openDeeplink', {
      app_name: 'Web3',
      deeplink: query && isInApp ? `${path}?${query}` : path, // 跳转到铭文搜索页面
    }).then((e: any) => {
      console.log(JSON.stringify(e));
      if (e?.params?.result) {
        router.push(normalizeLink(`/project-detail?tick=${e.params.result}`));
      }
    });
  } catch (error) {
    console.error(error);
  }
};

const gt = (version1: string, version2: string) => {
  const list1 = version1.split('.');
  const list2 = version2.split('.');
  for (let i = 0; i < list1.length; i++) {
    if (list1[i] && !list2[i]) {
      return true;
    }
    if (+list1[i] < +list2[i]) {
      return false;
    }
  }
  return true;
};

const gte = (version1: string, version2: string) => {
  const list1 = version1.split('.').map(Number); // 确保字符串转为数字
  const list2 = version2.split('.').map(Number);

  const maxLength = Math.max(list1.length, list2.length); // 确保可以比较长度不等的版本号

  for (let i = 0; i < maxLength; i++) {
    const v1 = list1[i] || 0; // 如果某个版本号位不存在，用0代替
    const v2 = list2[i] || 0;

    if (v1 > v2) {
      return true;
    } else if (v1 < v2) {
      return false;
    }
  }

  return true; // 全部相等时，也视为大于等于
};

export const versionDetect = (cb: (status: boolean) => void) => {
  const supportedVersion = '4.35.0';
  const supportedHideNavBarVersion = '4.35.0';
  try {
    bybitHecate('SystemInfo', 'getAppConfig', {}).then((res: any) => {
      const { appVersion = '' } = res?.params || res || {};
      const appSupported = gt(appVersion, supportedVersion);
      isSupportHideNavBar = gt(appVersion, supportedHideNavBarVersion);
      console.log(
        'appVersion:',
        appVersion,
        'appSupported:',
        appSupported,
        isSupportHideNavBar
      );
      // alert('appVersion:'+appVersion+",appSupported:"+appSupported);
      if (cb) {
        cb(appSupported);
      }
    });
  } catch (e) {
    console.error(e);
  }
};

export let isInApp: boolean = false;
export let isSupportHideNavBar: boolean = false;

export const detectIsInApp = () => {
  if (isBrowser) {
    versionDetect((status) => {
      isInApp = status;
    });
  }
};

const addFirstViewPathParams = (url: string = '') => {
  const hasQuery = url.includes('?');
  const div = hasQuery ? '&' : '?';
  const params = `${div}firstViewPath=${encodeURIComponent(url)}`;
  return `${url}${params}`;
};

export const openWebview = (link: string, hidden: boolean = true) => {
  const origin = window.location.origin;
  const path = addFirstViewPathParams(normalizeLink(link));
  const isFullUrl = link?.includes('http');
  let url = isFullUrl ? link : `${origin}${path}`;
  console.log('openWebview:', url);

  if (location.search) {
    const appendSearch = window.location.search.slice(1);
    const hasQuery = url.includes('?');
    url = `${url}${hasQuery ? '&' : '?'}${appendSearch}`;
  }

  try {
    bybitHecate('Web3WalletBridge', 'openDeeplink', {
      app_name: 'Web3',
      hiddenNaviBar: hidden && isSupportHideNavBar,
      deeplink: url, // 跳转到铭文搜索页面
    }).then((e: any) => {
      console.log(JSON.stringify(e));
    });
  } catch (error) {
    console.error(error);
  }
};

const parseSearch = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  const searchStr = window.location.search?.slice(1) || '';
  const parts = searchStr.split('&');
  const params: Record<string, string> = {};
  for (let i = 0; i < parts.length; i++) {
    const [key, value] = parts[i].split('=');
    params[key] = value;
  }
  return params;
};

export const useNav = () => {
  const router = useRouter();

  const goBack = () => {
    // alert('test go back');
    const params = parseSearch();
    const url = decodeURIComponent(params?.['firstViewPath']) || '';
    const isCurrentPage = params?.['firstViewPath']
      ? window.location.pathname === url.split('?')?.[0]
      : false;

    // console.log('url:', window.location.pathname, isCurrentPage);

    if (isCurrentPage) {
      closePage();
      return;
    }
    router.back();
  };

  const goDownload = () => {
    router.push(normalizeLink(downloadPath));
  };

  const navTo = (path: string) => {
    const fullPath = normalizeLink(path);
    if (location.search) {
      const appendSearch = window.location.search.slice(1);
      const hasQuery = fullPath.includes('?');
      const fullLink = `${fullPath}${hasQuery ? '&' : '?'}${appendSearch}`;

      router.push(fullLink);
      return;
    }
    router.push(fullPath);
  };

  return { goBack, goDownload, navTo };
};

//  bybitHecate('NavigationBar', 'setNavigationBarHidden', {isHidden:true})
export const closePage = () => {
  try {
    bybitHecate('Navigator', 'closePage', {}).then((e) => {
      console.log(JSON.stringify(e));
    });
  } catch (error) {
    console.error(error);
  }
};

export const hideNavBar = () => {
  if (!isSupportHideNavBar) {
    return;
  }
  try {
    bybitHecate('NavigationBar', 'hide', {}).then((e) => {
      console.log(JSON.stringify(e));
    });
  } catch (error) {
    console.error(error);
  }
};

export const setNavBarTitle = (title: string = '') => {
  try {
    document.title = title;
    bybitHecate('NavigationBar', 'setTitle', { title: title }).then((e) => {
      console.log(JSON.stringify(e));
    });
  } catch (error) {
    console.error(error);
  }
};

export const openBrowser = (
  url: string,
  external: boolean = false,
  hidden: boolean = false
) => {
  if (isInApp && !external) {
    openWebview(url, hidden || false);
    return;
  }
  try {
    bybitHecate('UrlLauncher', 'open', { url }).then((e) => {
      console.log(JSON.stringify(e));
    });
  } catch (error) {
    console.error(error);
  }
};

export const NEW_BY_APP_INNER_PROTOCOL = [
  'by-mini://',
  'by://',
  'http://',
  'https://',
];

export const newTriggerOneLink = (deeplink, webUrl = window.location.href) => {
  const newDeepLink = NEW_BY_APP_INNER_PROTOCOL.some((p) =>
    deeplink.startsWith(p)
  )
    ? `${BY_APP_PROTOCOL}open/route?targetUrl=${encodeURIComponent(deeplink)}`
    : deeplink;

  const link = `https://app.bybit.com/inapp?by_dp=${encodeURIComponent(newDeepLink)}&by_web_link=${encodeURIComponent(webUrl)}`;

  window.open(link);
};

/**
 * 兼容了app、h5、pc端的open方法
 * app内直接打开对应deeplink
 * h5和pc打开openlink
 */
export const hybirdOpen = (deeplink, webUrl) => {
  if (isApp()) {
    jumpApp({
      path: deeplink,
    });
    return;
  }

  if (!isApp() && isMobile) {
    newTriggerOneLink(deeplink, webUrl);
    return;
  }

  window.open(webUrl, '_blank');
};

export const jumpApp = async (params: object) => {
  if (isApp()) {
    await loadJsHecateScript();
    window.BybitHecate?.module('Router')
      .method('push')
      .invoke(params)
      .catch(({ message }: any) => {
        console.error(message);
      });
  }
};

export const h5Jump = (path: string) => {
  if (isApp()) {
    // app内
    if (path.startsWith('bybitapp://')) {
      // bybitapp协议,直接打开
      jumpApp({ path });
    } else if (path.startsWith('by-mini://')) {
      // by-mini地址,使用bybitapp协议包裹打开
      jumpApp({
        path: `bybitapp://open/route?targetUrl=${encodeURIComponent(path)}`,
      });
    } else {
      // http地址,使用webview打开, 如果通过 webview 打开，自己拼接 `bybitapp://open/web?url=${path}`
      openBrowser(path);
    }
  } else {
    // app外,尝试唤起app,否则下载
    triggerOneLink(path);
  }
};
